/** @jsx jsx */

import type { InputHTMLAttributes } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { jsx } from '@balance-web/core';
import { useFieldContext } from '@balance-web/field';
import { buildDataAttributes } from '@balance-web/core';
import type { WithDataAttributeProp } from '@balance-web/core';
import type { InputSizeType } from '@balance-web/text-input';
import { useInputStyles } from '@balance-web/text-input';
import { wrapHandlers } from '@balance-web/utils';

import type { ISODate } from './types';
import { formatValue, getExternalValue, validateShorthand } from './utils';

type InvalidProps = 'onChange' | 'placeholder' | 'size' | 'type' | 'value';
export type DateInputProps = WithDataAttributeProp<
  {
    onChange: (value: ISODate | undefined) => void;
    size?: InputSizeType;
    value: ISODate | undefined;
  } & Omit<InputHTMLAttributes<HTMLInputElement>, InvalidProps>
>;

// we're using a regular input instead of an input with type=date
// because type=date doesn't work in Safari for Mac
// and type=date in browsers that do support it is hard to use
// because it masks the value while typing

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      onBlur,
      onChange,
      size = 'medium',
      value,
      data,
      ...props
    }: DateInputProps,
    ref
  ) => {
    // @ts-ignore Santise invalid dom attributes
    if ('blur' in props && typeof props.blur === 'function') {
      delete props.blur;
    }

    const { invalid, ...a11yProps } = useFieldContext();
    const [internalValue, setInternalValue] = useState(() => {
      return formatValue(value);
    });
    const styles = useInputStyles({ size, shape: 'square' });

    // sync the internal string value with the consumer's ISO date
    // clears the value if a valid date isn't found
    useEffect(() => {
      const externalValue = getExternalValue(internalValue);
      if (externalValue !== value) {
        onChange(externalValue);
      }
    }, [internalValue, onChange, value]);

    // support 2 digit years e.g. "31/12/21" --> "31/12/2021"
    // clears the input if a valid date isn't found
    const handleBlur = wrapHandlers(onBlur, () => {
      if (value) {
        setInternalValue(validateShorthand);
      } else {
        setInternalValue('');
      }
    });

    const dataAttributes = buildDataAttributes(data);

    return (
      <input
        ref={ref}
        aria-invalid={invalid}
        css={styles}
        placeholder="dd/mm/yyyy"
        value={internalValue}
        onBlur={handleBlur}
        onChange={(event) => {
          setInternalValue(event.target.value);
        }}
        {...dataAttributes}
        {...props}
        {...a11yProps}
      />
    );
  }
);
